// Who has already seen the tour, and who has already seen the help panel.
//
// Both were remembered in `localStorage` alone, and staff reported the tour
// and the "how this works" card coming back every few days — "if I close it
// and refresh the page it remembers, but after a while it'll pop up again".
//
// **That reproduction is the diagnosis.** It survives a refresh, so the write
// worked; it dies later, so the store was thrown away. This portal runs in a
// cross-site iframe under WordPress, where storage is *partitioned* — its own
// bucket per embedding site — and a partitioned bucket is evicted far more
// readily than a first-party one: Safari's ITP caps it at seven days without a
// top-level interaction, and "clear site data on quit" takes it nightly. It is
// the same trap `matterHref` in `App.js` already records about links.
//
// No amount of care with the local write survives the bucket being deleted, so
// the record moved to the middleware: one document for the office, keyed on
// the file handler's name off the embed's own `?user=`.
//
// **The browser is still asked, and either answer is enough.** Somebody who
// dismissed a card before this existed has it only in `localStorage`, and the
// office record knows nothing about them — asking the server alone would nag
// the whole office once on the deploy that fixed the nagging. And a fetch that
// fails leaves the local answer standing, which is exactly today's behaviour.
//
// **The cost is that the gate stops being synchronous.** A card can no longer
// open on the first render, because the office's answer has not arrived — so
// it opens a beat later, and defaults to *not shown* while the answer is in
// flight. That is the right way round: showing a card to somebody who has
// already dismissed it is the fault being fixed.

import { useEffect } from "react";
import { MIDDLEWARE_URL } from "./shared";

// The cards the middleware knows about. Its `SEEN_KINDS` is the authority and
// refuses anything else outright; this is here so a typo is a test failure
// rather than a 400 nobody is watching for.
export const ONBOARDING_KINDS = ["tour", "help"];

// Who is looking, and the key to ask with. **Injected rather than read here**:
// `readParams()` lives in `App.js` and the token is taken out of the visible
// URL and kept in `sessionStorage` under a key that file owns, so reaching for
// either from here would be a second copy of both.
let who = { user: "", token: "" };
export const configureOnboarding = (config) => {
  who = { user: config?.user || "", token: config?.token || "" };
};

// One request, shared by both cards. They mount at different moments on
// different screens, and two GETs for one answer is two.
let asking = null;

// Only for a test that needs a fresh page's answer.
export const forgetOnboarding = () => { asking = null; };

const headers = () => (who.token ? { "x-portal-token": who.token } : {});

// What the office says, or null where there is no answer to be had: nobody to
// ask about, no `fetch`, a middleware that is down, or a name the middleware
// could make nothing of. **Null is "fall back to the browser"**, never "seen
// nothing" — those are different, and reading a failure as the second is how a
// card gets shown to somebody who dismissed it this morning.
export const askOnboarding = () => {
  if (asking) return asking;
  if (!who.user || typeof fetch !== "function") {
    asking = Promise.resolve(null);
    return asking;
  }

  asking = fetch(
    `${MIDDLEWARE_URL}/onboarding?user=${encodeURIComponent(who.user)}`,
    { headers: headers() },
  )
    .then((r) => r.json())
    .then((data) => (data?.ok && data.key ? data.seen || null : null))
    .catch(() => null);

  return asking;
};

// Tell the office. **Fire and forget, and it swallows its own failure**: a
// card was dismissed whatever this manages to record, and the local write
// beside it has already happened — the same rule `recordEvent` follows about
// a log write never taking down the work it describes.
//
// It also updates the answer already in hand, so a second card mounting after
// this one sees it without asking again.
export const markSeen = (kind) => {
  if (!who.user || typeof fetch !== "function") return;

  if (asking) {
    asking = asking.then((seen) => (seen ? { ...seen, [kind]: true } : seen));
  }

  try {
    fetch(`${MIDDLEWARE_URL}/onboarding`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers() },
      body: JSON.stringify({ user: who.user, seen: [kind] }),
    }).catch(() => {});
  } catch (e) { /* the local write is the fallback */ }
};

// Run `onFirstVisit` once, if neither the office nor this browser has a record
// of this card being dismissed.
//
// **It never fires twice and never fires late.** The effect runs once and
// drops its answer if the component has gone — a card opening under somebody
// who navigated away is worse than one that never opened.
export const useFirstVisit = (kind, seenLocally, onFirstVisit, { skip = false } = {}) => {
  useEffect(() => {
    if (skip) return undefined;
    let live = true;
    askOnboarding().then((office) => {
      if (!live) return;
      // Either answer is enough. The office is asked first because it is the
      // one that survives; the browser is what answers for everybody who
      // dismissed a card before the office had a record of it.
      if (office && office[kind] === true) return;
      if (seenLocally()) return;
      onFirstVisit();
    });
    return () => { live = false; };
    // Deliberately once: the whole question is what this browser and the
    // office knew when the screen opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
